import { initialStateModal } from 'components/dynamicTab'
import { ElementComponent } from 'components/dynamicTab/elementComponent'
import { Icon } from 'components/icon'
import { Input } from 'components/input'
import { ModalConfirm } from 'components/modal'
import { handleValid } from 'components/myOrganization'
import { SetUpInfoTab } from 'components/setUpInfoTab'
import { Typography } from 'components/typography'
import { Icons, TabMyOrganizationItem, TypographyTypes } from 'interfaces'
import get from 'lodash/get'
import React, { FC, useEffect, useState } from 'react'
import { Controller, Path, useWatch } from 'react-hook-form'
import { configGenaral } from 'utils'
import { ElementItemSubMenuProps } from './SubMenuTabItem.type'

export const ElementItemSubMenu: FC<ElementItemSubMenuProps> = (
	props
): JSX.Element => {
	const {
		name,
		menuIndex,
		errors,
		control,
		fieldError,
		isValid,
		remove,
		register,
		setValue,
		watch,
		setErrorForm,
		setFieldError,
		saveData
	} = props
	const [modalProps, setModalProps] = useState(initialStateModal)
	const enabledLink = useWatch({
		control,
		name: `${name}.sub_menus.${menuIndex}.setup_card.link.enabled_link` as Path<{
			tabs: TabMyOrganizationItem[]
		}>
	})

	const errorName = get(errors, `${name}.sub_menus.${menuIndex}.name`)
	const errorLink = get(
		errors,
		`${name}.sub_menus.${menuIndex}.setup_card.link.url`
	)

	const rules = {
		name: {
			required: {
				value: true,
				message: 'This field is required'
			}
		},
		url: {
			required: {
				value: enabledLink,
				message: 'Is required'
			},
			maxLength: {
				value: 512,
				message: 'URL too large. Maximum 512 characters.'
			},
			pattern: {
				value: /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/,
				message: 'Please enter a valid url'
			}
		}
	}
	const handleDelectExternalLink = (): void => {
		const routeLink = `${name}.sub_menus.${menuIndex}.setup_card.link`
		setValue(
			`${routeLink}.url` as Path<{
				tabs: TabMyOrganizationItem[]
			}>,
			''
		)
		setValue(
			`${routeLink}.enabled_link` as Path<{
				tabs: TabMyOrganizationItem[]
			}>,
			false
		)
		setModalProps(initialStateModal)
	}

	const nameType = `${name}.sub_menus.${menuIndex}`
	const dataInfoData = watch()
	const linkInfo =
		watch(
			`${nameType}.setup_card.link.enabled_link` as Path<{
				tabs: TabMyOrganizationItem[]
			}>
		) ?? false
	const urlInfo =
		watch(
			`${nameType}.setup_card.link.url` as Path<{
				tabs: TabMyOrganizationItem[]
			}>
		) ?? ''
	const components =
		watch(
			`${nameType}.components` as Path<{
				tabs: TabMyOrganizationItem[]
			}>
		) ?? []

	const validateMenuData = (): boolean => {
		if (linkInfo && !String(urlInfo).trim()) {
			return false
		}

		if (!linkInfo && Array.isArray(components) && components.length === 0) {
			return false
		}

		return true
	}

	useEffect(() => {
		const isValidMenu = validateMenuData()
		if (fieldError.includes(nameType) && isValidMenu) {
			handleValid(nameType, true, setFieldError)
		} else if (!fieldError.includes(nameType) && !isValidMenu) {
			handleValid(nameType, false, setFieldError)
		}
	}, [dataInfoData, linkInfo, urlInfo, components, isValid])

	const confirmChangeStateLink = (): void => {
		setModalProps({
			open: true,
			title: 'External Redirection will be deleted!',
			description:
				'By disabling the External Redirection it will be deleted, do you want to continue?',
			onConfirm: () => {
				handleDelectExternalLink()
			}
		})
	}

	const confirmRemoveSubMenu = (): void => {
		setModalProps({
			open: true,
			title: 'Sub menu will be deleted!',
			description:
				'By deleting the Sub menu all contents and menu customization will be deleted, do you want to continue?',
			onConfirm: () => {
				const updatedErrors = fieldError
					.filter(error => !error.includes(nameType))
					.map(error => {
						return error.replace(/sub_menus\.(\d+)/, (_, num) => {
							const newIndex =
								parseInt(num, 10) > menuIndex ? parseInt(num, 10) - 1 : num
							return `sub_menus.${newIndex}`
						})
					})
				setFieldError(updatedErrors)
				handleValid(nameType, true, setFieldError)
				remove(menuIndex)
				setModalProps(initialStateModal)
			}
		})
	}

	const confirmHandleEnabledRediretion = (value: boolean): void => {
		setModalProps({
			open: true,
			title: 'Components Will Be Deleted',
			description:
				'By enabling the External Redirection option all components will be deleted, do you want to continue?',
			onConfirm: () => {
				setValue(
					`${nameType}.components` as Path<{
						tabs: TabMyOrganizationItem[]
					}>,
					[]
				)
				setValue(
					`${name}.sub_menus.${menuIndex}.setup_card.link.enabled_link` as Path<{
						tabs: TabMyOrganizationItem[]
					}>,
					value
				)
				setModalProps(initialStateModal)
			}
		})
	}

	return (
		<div className="my-4 p-4 rounded-lg bg-white border-[1px] border-blue-primary">
			<div
				className="px-4 pb-6 rounded-lg bg-white border-[1px] border-blue-primary"
				style={{ boxShadow: '0px 3px 3px rgba(0, 0, 0, 0.1)' }}
			>
				<div className="w-full py-3 flex justify-between items-center">
					<Typography
						title={`Sub Menu ${menuIndex + 1}`}
						fontLeading="22/27"
						type={TypographyTypes.h2}
						className="text-gray-16"
					/>
					<div
						aria-hidden="true"
						className="z-10 cursor-pointer flex items-center"
						onClick={confirmRemoveSubMenu}
					>
						<Icon
							src={Icons.delete}
							className="w-7 ml-3 text-gray-1 "
							fillPath
						/>
						<Typography
							title="Remove"
							fontLeading="12/12"
							type={TypographyTypes.h2}
							className="text-gray-1"
						/>
					</div>
				</div>

				<Input
					name={`${name}.sub_menus.${menuIndex}.name`}
					min={1}
					register={register}
					rules={rules.name}
					error={errorName}
					borderFull={false}
					inputClassName="rounded-lg border-[1px] border-gray-17"
					limit
					cantLimit={configGenaral.maxTabNumber}
					setValueInput={setValue}
					autoFocus
					watchValue={String(
						watch(
							`${name}.sub_menus.${menuIndex}.name` as Path<{
								tabs: TabMyOrganizationItem[]
							}>
						)
					)}
				/>

				<div className="flex md:flex-row flex-col gap-2 mt-4">
					<div className="w-full">
						<Controller
							control={control}
							name={
								`${name}.sub_menus.${menuIndex}.setup_card.link.enabled_link` as Path<{
									tabs: TabMyOrganizationItem[]
								}>
							}
							render={({ field: { value, onChange } }) => (
								<SetUpInfoTab
									title="Set external URL:"
									subTitle="External Redirection"
									icon={Icons.portal}
									value={Boolean(value)}
									setValue={enabledUrl => {
										if (!enabledUrl) {
											confirmChangeStateLink()
										} else if (
											Array.isArray(components) &&
											components.length > 0
										) {
											confirmHandleEnabledRediretion(enabledUrl)
										} else {
											onChange(enabledUrl)
										}
									}}
								/>
							)}
						/>
						{enabledLink && (
							<Controller
								control={control}
								name={
									`${name}.sub_menus.${menuIndex}.setup_card.link.url` as Path<{
										tabs: TabMyOrganizationItem[]
									}>
								}
								render={() => {
									return (
										<Input
											name={`${name}.sub_menus.${menuIndex}.setup_card.link.url`}
											register={register}
											rules={rules.url}
											error={errorLink}
											borderFull={false}
											inputClassName="rounded-lg border-[1px] border-gray-17"
											autoFocus
										/>
									)
								}}
							/>
						)}
					</div>
				</div>
			</div>
			{!enabledLink && (
				<div className="my-2 px-4 rounded-lg bg-white border-[1px] border-blue-primary">
					<ElementComponent
						name={
							`${name}.sub_menus.${menuIndex}.components` as Path<{
								tabs: TabMyOrganizationItem[]
							}>
						}
						active={menuIndex}
						nameCard={`Sub Menu ${menuIndex + 1} Content`}
						register={register}
						errors={errors}
						setValue={setValue}
						watch={watch}
						control={control}
						saveData={saveData}
						setFieldError={setFieldError}
						setErrorForm={setErrorForm}
						isValid={isValid}
						fieldError={fieldError}
						resourceUpdated
					/>
				</div>
			)}

			<ModalConfirm
				open={modalProps.open}
				setOpen={() => setModalProps(initialStateModal)}
				hasCancel
				buttonLabelCancel="Cancel"
				buttonLabel="Yes"
				flexButton="row"
				handleCancel={() => setModalProps(initialStateModal)}
				onClickConfirm={modalProps.onConfirm}
				icon={Icons.warningActive}
				title={modalProps.title}
				descripcion={modalProps.description}
				classNameButton="h-[32px] w-[151px] m-0 !py-0"
			/>
		</div>
	)
}
